import { Component, Input } from '@angular/core';
import { CommonDialoguesService } from '@flyfreely-portal-ui/common-dialogues';
import {
    CreateFlightLogCollectionSourceCommand,
    DO_NOTHING,
    FlightLogCollectionService,
    FlightLogCollectionSourceDto,
    FlyFreelyError,
    FlyFreelyLoggingService,
    OrganisationAutomaticMissionConfigurationDto,
    PersonRolesDto,
    PersonService,
    PersonWithRoles,
    UpdateFlightLogCollectionSourceCommand,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import {
    NgSelectPaginationValues,
    NgSelectSearch,
    ngSelectPreSearchConfigure,
    updateNgSelectData
} from '@flyfreely-portal-ui/ng-select';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { Observable, Subject, firstValueFrom } from 'rxjs';
import { debounceTime, map, takeUntil } from 'rxjs/operators';
import { FlightLogSettingsService } from './flight-log-settings.service';
import { FlightLogCollectionStatusLookup } from './helpers';
import { FlightLogCollectionSourceWithEnabled } from './interfaces';

function addDjiFields(showPersonalAccountOption: boolean): FormlyFieldConfig[] {
    return [
        {
            key: 'username',
            type: 'input',
            props: {
                label: 'Email address',
                required: true,
                description:
                    'The email address you use to sign into DJI Go/Pilot/Pilot2'
            }
        } as FormlyFieldConfig,
        {
            key: 'password',
            type: 'input',
            props: {
                label: 'Password',
                required: true,
                type: 'password',
                description: 'The password for your DJI sync account'
            }
        }
    ].concat(
        showPersonalAccountOption
            ? [
                  {
                      key: 'personalAccount',
                      type: 'radio',
                      props: {
                          label: 'Personal Account',
                          required: true,
                          description:
                              'Is this your personal DJI sync account, or is it an organisation wide account?',
                          options: [
                              { name: 'Personal Account', value: true },
                              { name: 'Organisation Account', value: false }
                          ]
                      }
                  }
              ]
            : []
    );
}

/**
 * Show the DJI collection source list. It is assumed that if this component is embedded then the user can at least manage their own collection sources.
 */
@Component({
    selector: 'dji-sync-flight-reader-settings',
    template: `<div class="horizontal-container">
        <button
            type="button"
            class="btn-circle btn-link"
            (click)="addDji()"
            [disabled]="working"
        >
            <span class="fa fa-plus"></span>
        </button>
        <div
            *ngIf="djiList$ | async as djiList"
            class="vertical-container fill"
        >
            <table class="table">
                <thead>
                    <tr>
                        <th style="width: 20%">Username</th>
                        <th style="width: 20%">Owner</th>
                        <th style="width: 20%">Last Completion Time</th>
                        <th style="width: 20%">Last Completion Message</th>
                        <th style="width: 5%">Enabled</th>
                        <th
                            *ngIf="
                                flightLogSettingsService.canUseAlerts &&
                                canManageAll
                            "
                            style="width: 5%"
                        >
                            Alerts
                        </th>
                        <th
                            style="width: 10%"
                            *ngIf="hasMissionConfig && canManageAll"
                        >
                            Automatic Mission Creation
                        </th>
                        <th style="width: 10%"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr *ngFor="let account of djiList">
                        <td>{{ account.username }}</td>
                        <td>
                            <ng-container [ngSwitch]="canManageAll">
                                <ng-container *ngSwitchCase="true">
                                    <editable
                                        [initialContext]="{
                                            id: account.id,
                                            ownerId: account.owner?.id
                                        }"
                                        [updateFn]="reassignSource"
                                    >
                                        <ng-template viewMode>
                                            <person-avatar
                                                [person]="account.owner"
                                                [managingOrganisationId]="
                                                    flightLogSettingsService.organisationId
                                                "
                                                alternativeName="Organisation"
                                                [width]="30"
                                                [height]="30"
                                            >
                                            </person-avatar>
                                        </ng-template>
                                        <ng-template
                                            editMode
                                            let-context="context"
                                            let-state="state"
                                            let-submit="submit"
                                        >
                                            <ng-select
                                                [(ngModel)]="context.ownerId"
                                                [items]="
                                                    syncAssigneeCandidateList
                                                "
                                                bindValue="id"
                                                appendTo="body"
                                                [readonly]="state.saving"
                                                [loading]="loadingPersonnel"
                                                [virtualScroll]="true"
                                                (change)="submit()"
                                                (scrollToEnd)="
                                                    onPersonSelectScroll()
                                                "
                                                (search)="
                                                    onPersonSelectSearch($event)
                                                "
                                                (close)="onPersonSelectClose()"
                                                (open)="onPersonSelectOpen()"
                                            >
                                                <ng-template
                                                    ng-option-tmp
                                                    let-owner="item"
                                                    let-index="index"
                                                >
                                                    <person-avatar
                                                        [person]="owner"
                                                        [managingOrganisationId]="
                                                            flightLogSettingsService.organisationId
                                                        "
                                                        [width]="25"
                                                        [height]="25"
                                                    >
                                                    </person-avatar>
                                                </ng-template>
                                                <ng-template
                                                    ng-label-tmp
                                                    let-owner="item"
                                                >
                                                    <person-avatar
                                                        [person]="owner"
                                                        [managingOrganisationId]="
                                                            flightLogSettingsService.organisationId
                                                        "
                                                        [width]="20"
                                                        [height]="20"
                                                    >
                                                    </person-avatar>
                                                </ng-template>
                                            </ng-select>
                                            <div
                                                *ngIf="state.error != null"
                                                class="text-danger"
                                            >
                                                {{ state.error }}
                                            </div>
                                        </ng-template>
                                    </editable>
                                </ng-container>
                                <ng-container *ngSwitchCase="false">
                                    {{
                                        (account.owner | formatPerson) ||
                                            'Organisation'
                                    }}
                                </ng-container>
                            </ng-container>
                        </td>
                        <td>
                            <ng-container
                                *ngIf="account.lastCompletionTime != null"
                            >
                                {{
                                    account.lastCompletionTime | formatDateTime
                                }}
                                -
                            </ng-container>
                            {{ flightLogStatus[account.lastCompletionStatus] }}
                        </td>
                        <td>
                            {{ account | formatFlightLogCompletionMessage }}
                        </td>
                        <td *ngIf="account.collectNow">
                            {{
                                account.collectNowQueue
                                    | formatFlightLogQueuePosition
                            }}
                        </td>
                        <td *ngIf="!account.collectNow">
                            <mat-slide-toggle
                                [disabled]="working"
                                [(ngModel)]="account.enabled"
                                (change)="updateDjiEnabled(account)"
                            >
                            </mat-slide-toggle>
                        </td>
                        <td
                            *ngIf="
                                flightLogSettingsService.canUseAlerts &&
                                canManageAll
                            "
                        >
                            <flight-log-alert
                                [alertSettings]="account.alertSettings"
                                (showSettings)="
                                    flightLogSettingsService.showAlertSettings(
                                        account
                                    )
                                "
                            ></flight-log-alert>
                        </td>
                        <td *ngIf="hasMissionConfig && canManageAll">
                            <automatic-mission-assignment-settings
                                [entry]="account"
                                [defaultOrganisationConfig]="
                                    defaultOrganisationConfig
                                "
                            ></automatic-mission-assignment-settings>
                        </td>
                        <td class="actions">
                            <button
                                type="button"
                                class="btn btn-tertiary"
                                (click)="collectNow(account)"
                                [disabled]="working || account.collectNow"
                                tooltip="Collect now"
                                placement="left"
                            >
                                <span class="fal fa-sync"></span>
                            </button>
                            <button
                                type="button"
                                class="btn btn-tertiary"
                                (click)="editDji(account)"
                                [disabled]="working"
                                tooltip="Edit"
                                placement="left"
                            >
                                <span class="fal fa-pencil-alt"></span>
                            </button>
                            <button
                                type="button"
                                class="btn btn-tertiary"
                                (click)="convert(account)"
                                [disabled]="working"
                                tooltip="Convert to DJI Cloud source"
                                placement="left"
                            >
                                <span class="fal fa-transporter-2"></span>
                            </button>

                            <button
                                class="btn btn-tertiary btn-delete"
                                type="button"
                                (click)="deleteDji(account)"
                                [disabled]="working"
                                tooltip="Delete"
                                placement="left"
                            >
                                <span class="fal fa-trash-alt"></span>
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>
            <div class="bottom-buffer fill" *ngIf="djiList.length === 0">
                <empty componentName="empty-flight-reader-dji-accounts">
                    <button
                        type="button"
                        class="btn btn-default top-buffer"
                        (click)="addDji()"
                    >
                        Add DJI Account
                    </button>
                </empty>
            </div>
        </div>
    </div>`
})
export class DjiSyncFlightReaderSettings {
    @Input() hasMissionConfig = false;
    @Input() canManageAll = false;
    @Input()
    defaultOrganisationConfig: OrganisationAutomaticMissionConfigurationDto;

    private ngUnsubscribe$ = new Subject<void>();

    djiList$: Observable<FlightLogCollectionSourceWithEnabled[]>;

    working: boolean;

    collectionFrequency =
        UpdateFlightLogCollectionSourceCommand.CollectionFrequency;

    flightLogStatus = FlightLogCollectionStatusLookup;

    syncAssigneeCandidateList: PersonWithRoles[] = [];

    loadingPersonnel = false;

    private personSearchSource = new Subject<NgSelectSearch>();

    personnelSelectConfig: NgSelectPaginationValues = {
        currentPage: 0,
        preSearchPage: null,
        searchValue: null,
        totalItems: null
    };

    private preloadedOwnerIds = new Set<number>();

    constructor(
        public flightLogSettingsService: FlightLogSettingsService,
        private workTracker: WorkTracker,
        private commonDialoguesService: CommonDialoguesService,
        private flightLogCollectionService: FlightLogCollectionService,
        private logging: FlyFreelyLoggingService,
        private personService: PersonService
    ) {
        this.djiList$ =
            this.flightLogSettingsService.flightLogCollectionSourceList$.pipe(
                takeUntil(this.ngUnsubscribe$),
                map(sources =>
                    sources
                        .filter(
                            s =>
                                s.type ===
                                FlightLogCollectionSourceDto.Type
                                    .DJI_FLIGHT_READER
                        )
                        .map(s => ({
                            ...s,
                            enabled: s.collectionFrequency !== 'NEVER'
                        }))
                )
            );
    }

    ngOnInit() {
        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => (this.working = working));

        if (this.canManageAll) {
            this.djiList$
                .pipe(takeUntil(this.ngUnsubscribe$))
                .subscribe(djiList => {
                    const ownerIds = djiList
                        .map(account => account.owner?.id)
                        .filter(
                            id => id != null && !this.preloadedOwnerIds.has(id)
                        );

                    if (ownerIds.length > 0) {
                        ownerIds.forEach(id => this.preloadedOwnerIds.add(id));
                        this.refreshPersonnelList(ownerIds);
                    }
                });

            this.personSearchSource
                .pipe(debounceTime(300), takeUntil(this.ngUnsubscribe$))
                .subscribe(search => {
                    this.personnelSelectConfig = ngSelectPreSearchConfigure(
                        search,
                        this.personnelSelectConfig
                    );
                    this.refreshPersonnelList();
                });
        } else {
            this.syncAssigneeCandidateList = [];
        }
    }

    ngOnDestroy() {
        this.personSearchSource.complete();
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    private refreshPersonnelList(preloadIds?: number[]) {
        this.loadingPersonnel = true;

        this.personService
            .findPersonnelGraphQL(
                this.flightLogSettingsService.organisationId,
                this.personnelSelectConfig.currentPage,
                15,
                {
                    roleOrList: [
                        PersonRolesDto.Roles.FLIGHT_LOG_COLLECTION_OWN
                    ],
                    textSearch:
                        this.personnelSelectConfig.searchValue || undefined,
                    idList: preloadIds
                }
            )
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: results => {
                    const newPersonnel = results.results.filter(
                        p =>
                            this.syncAssigneeCandidateList.findIndex(
                                person => person.id === p.id
                            ) === -1
                    );
                    this.syncAssigneeCandidateList =
                        this.syncAssigneeCandidateList.concat(newPersonnel);

                    if (preloadIds == null) {
                        this.personnelSelectConfig = updateNgSelectData(
                            results,
                            this.personnelSelectConfig
                        );
                    }
                    this.loadingPersonnel = false;
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error loading personnel: ${error.message}`
                    );
                    this.loadingPersonnel = false;
                }
            });
    }

    onPersonSelectScroll() {
        if (
            this.personnelSelectConfig.totalItems == null ||
            this.syncAssigneeCandidateList.length !==
                this.personnelSelectConfig.totalItems
        ) {
            this.refreshPersonnelList();
        }
    }

    onPersonSelectOpen() {
        if (
            this.personnelSelectConfig.totalItems == null ||
            this.syncAssigneeCandidateList.length !==
                this.personnelSelectConfig.totalItems
        ) {
            this.refreshPersonnelList();
        }
    }

    onPersonSelectSearch(search: NgSelectSearch) {
        this.personSearchSource.next(search);
    }

    onPersonSelectClose() {
        this.personnelSelectConfig.searchValue = null;
    }
    // FIXME the new entry isn't showing up
    addDji() {
        this.commonDialoguesService
            .showFormlyDialogue(
                'Create DJI',
                'Save',
                true,
                true,
                addDjiFields(this.canManageAll),
                {
                    username: null as string,
                    password: null as string,
                    personalAccount: false
                },
                data =>
                    firstValueFrom(
                        this.flightLogCollectionService.create({
                            organisationId:
                                this.flightLogSettingsService.organisationId,
                            type: CreateFlightLogCollectionSourceCommand.Type
                                .DJI_FLIGHT_READER,
                            collectionFrequency:
                                CreateFlightLogCollectionSourceCommand
                                    .CollectionFrequency.DAILY,
                            username: data.username,
                            password: data.password,
                            personalAccount:
                                !this.canManageAll || data.personalAccount
                        })
                    ),
                'modal-task'
            )
            .then(() => this.logging.success('DJI account setup'))
            .catch(DO_NOTHING);
    }

    collectNow(entry: FlightLogCollectionSourceDto) {
        this.flightLogCollectionService
            .collectNow(entry.id)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(updated =>
                this.logging.success('DJI collection scheduled')
            )
            .add(this.workTracker.createTracker());
    }

    editDji(entry: FlightLogCollectionSourceDto) {
        this.commonDialoguesService
            .showFormlyDialogue(
                'Update details',
                'Save',
                true,
                true,
                [
                    {
                        key: 'password',
                        type: 'input',
                        props: {
                            label: 'Password',
                            required: false,
                            type: 'password'
                        }
                    },
                    // Allowing to set enabled here and in the table for now
                    {
                        key: 'enabled',
                        type: 'radio',
                        defaultValue: true,
                        props: {
                            label: 'Enabled',
                            required: true,
                            options: [
                                { value: true, name: 'Enabled' },
                                { value: false, name: 'Disabled' }
                            ]
                        }
                    }
                ],
                {
                    password: null as string,
                    enabled:
                        entry.collectionFrequency ===
                        this.collectionFrequency.DAILY
                },
                data =>
                    firstValueFrom(
                        this.flightLogCollectionService.updateSource(
                            {
                                collectionFrequency:
                                    data.enabled === true
                                        ? this.collectionFrequency.DAILY
                                        : this.collectionFrequency.NEVER,
                                id: entry.id,
                                password: data.password ?? null
                            },
                            entry.id
                        )
                    ),
                'modal-task'
            )
            .then(() => this.logging.success('DJI account updated'))
            .catch(DO_NOTHING);
    }

    updateDjiEnabled(entry: FlightLogCollectionSourceWithEnabled) {
        this.flightLogCollectionService
            .updateSource(
                {
                    collectionFrequency: entry.enabled
                        ? this.collectionFrequency.DAILY
                        : this.collectionFrequency.NEVER,
                    id: entry.id,
                    password: null
                },
                entry.id
            )
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: () =>
                    this.logging.success(
                        'Flight log collection settings updated'
                    ),
                error: (error: FlyFreelyError) =>
                    this.logging.error(
                        error,
                        `Error updating flight log collection settings: ${error.message}`
                    )
            })
            .add(this.workTracker.createTracker());
    }

    deleteDji(entry: FlightLogCollectionSourceDto) {
        this.commonDialoguesService
            .showConfirmationDialogue(
                'Delete Collection',
                'Are you sure you want to delete this collection?',
                'Delete',
                () => Promise.resolve()
            )
            .then(() =>
                this.flightLogCollectionService
                    .deleteSource(entry.id)
                    .pipe(takeUntil(this.ngUnsubscribe$))
                    .subscribe({
                        next: () => this.logging.success('DJI account deleted'),
                        error: (error: FlyFreelyError) =>
                            this.logging.error(
                                error,
                                `Error deleting flight log collection settings: ${error.message}`
                            )
                    })
                    .add(this.workTracker.createTracker())
            )
            .catch(DO_NOTHING);
    }

    convert(entry: FlightLogCollectionSourceDto) {
        this.commonDialoguesService
            .showConfirmationDialogue(
                'Convert Source Type',
                'Are you sure you want to convert this to a DJI Cloud account?',
                'Convert',
                () => Promise.resolve()
            )
            .then(() =>
                this.flightLogCollectionService
                    .convertSource(entry.id, { type: 'DJI_CLOUD' })
                    .pipe(takeUntil(this.ngUnsubscribe$))
                    .subscribe({
                        next: () =>
                            this.logging.success(
                                'Account converted to DJI Cloud'
                            ),
                        error: (error: FlyFreelyError) =>
                            this.logging.error(
                                error,
                                `Error converting flight log collection settings: ${error.message}`
                            )
                    })
                    .add(this.workTracker.createTracker())
            )
            .catch(DO_NOTHING);
    }

    reassignSource = (context: { id: number; ownerId?: number }) =>
        firstValueFrom(
            this.flightLogCollectionService
                .assignFlightLogCollectionSource(context.id, {
                    personId: context.ownerId
                })
                .pipe(takeUntil(this.ngUnsubscribe$))
        );
}
